/* eslint-disable @typescript-eslint/lines-between-class-members */

import { CompositeLayerProps } from '@deck.gl/core/typed';
import Supercluster, { AnyProps, PointFeature } from 'supercluster';
import { BBox } from 'geojson';

type LayerProps = CompositeLayerProps & { clusterRadius: number };

const DEFAULT_CLUSTER_RADIUS = 95;
const MAX_ZOOM_LEVEL = 19;
const STREET_CLUSTER_RADIUS = 1;
const STREET_ZOOM_LEVEL = 13;

const createSupercluster = (radius = DEFAULT_CLUSTER_RADIUS): Supercluster => {
  return new Supercluster({
    generateId: true,
    maxZoom: MAX_ZOOM_LEVEL,
    radius,
  });
};

class ClusterIndexManager {
  private static instances: Map<string, ClusterIndexManager> = new Map<string, ClusterIndexManager>();

  private readonly name: string;
  private readonly streetLevelIndex: Supercluster;
  private readonly defaultIndex: Supercluster;
  private hasDataLoaded: boolean = false;

  static getInstance(name: string, propObjects: LayerProps): ClusterIndexManager {
    if (!ClusterIndexManager.instances.has(name)) {
      ClusterIndexManager.instances.set(name, new ClusterIndexManager(name, propObjects));
    }

    return <ClusterIndexManager>ClusterIndexManager.instances.get(name);
  }

  private constructor(name: string, propObjects: LayerProps) {
    this.name = name;
    this.defaultIndex = createSupercluster(propObjects.clusterRadius || DEFAULT_CLUSTER_RADIUS);
    this.streetLevelIndex = createSupercluster(STREET_CLUSTER_RADIUS);
  }

  public getIndexBasedOnZoom(zoom: number): Supercluster {
    return zoom > STREET_ZOOM_LEVEL ? this.streetLevelIndex : this.defaultIndex;
  }

  public loadData<T extends PointFeature<AnyProps>>(data: T[]): void {
    this.defaultIndex.load(data);
    this.streetLevelIndex.load(data);
    this.hasDataLoaded = true;
  }

  public getWorldClusters(zoom: number): PointFeature<AnyProps>[] {
    return this.getViewportClusters([-180, -85, 180, 85], zoom);
  }

  public getViewportClusters(viewport: BBox, zoom: number): PointFeature<AnyProps>[] {
    try {
      return this.getIndexBasedOnZoom(zoom).getClusters(viewport, zoom);
    } catch {
      return [];
    }
  }

  public getClusterLeaves(cluster: PointFeature<AnyProps>, zoom: number): PointFeature<AnyProps>[] {
    if (!cluster?.properties?.cluster) return [];
    return (
      this.getIndexBasedOnZoom(zoom).getLeaves(
        cluster.properties?.cluster_id ?? cluster.id,
        cluster.properties.point_count,
      ) ?? []
    );
  }

  public isReady(): boolean {
    return this.hasDataLoaded;
  }

  public clear(): void {
    if (ClusterIndexManager.instances.has(this.name)) {
      ClusterIndexManager.instances.delete(this.name);
    }
  }
}

export default ClusterIndexManager;

/* eslint-enable @typescript-eslint/lines-between-class-members */
